import { execFile, type ChildProcess, type ChildProcessByStdio } from "node:child_process";
import fs from "node:fs";
import path from "node:path";
import { Readable, Writable } from "node:stream";

export const PROCESS_HELPER_TIMEOUT_MS = 8_000;
export const PROCESS_HELPER_MAX_BUFFER_BYTES = 32 * 1024 * 1024;

export async function runTimedExecFile(
  command: string,
  args: readonly string[],
  options: {
    timeoutMs?: number;
    maxBufferBytes?: number;
    windowsHide?: boolean;
    env?: NodeJS.ProcessEnv;
  } = {},
): Promise<string> {
  const timeoutMs = Math.max(1, Math.round(options.timeoutMs ?? PROCESS_HELPER_TIMEOUT_MS));
  return await new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const child = execFile(
      command,
      [...args],
      {
        encoding: "utf8",
        maxBuffer: options.maxBufferBytes ?? PROCESS_HELPER_MAX_BUFFER_BYTES,
        killSignal: "SIGKILL",
        windowsHide: options.windowsHide,
        env: options.env,
      },
      (error, stdout) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timer) {
          clearTimeout(timer);
        }
        if (error) {
          reject(error);
          return;
        }
        resolve(stdout);
      },
    );

    timer = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      const killed = child.kill("SIGKILL");
      child.stdout?.destroy();
      child.stderr?.destroy();
      child.unref();
      reject(
        Object.assign(new Error(`${command} timed out after ${timeoutMs}ms`), {
          code: "ETIMEDOUT",
          killed,
          signal: "SIGKILL" as const,
        }),
      );
    }, timeoutMs);
  });
}

export type CommandParts = {
  command: string;
  args: string[];
};

export function normalizeAgentCommandInput(value: string | readonly string[]): {
  agentCommand: string;
  agentArgv?: string[];
} {
  if (typeof value === "string") {
    return { agentCommand: value };
  }
  const parts = toCommandParts([...value]);
  const argv = [parts.command, ...parts.args];
  return {
    agentCommand: renderArgvIdentity(argv),
    agentArgv: argv,
  };
}

const IDENTITY_SAFE_ARG_RE = /^[A-Za-z0-9_@%+=:,./^~-]+$/u;

export function renderArgvIdentity(argv: readonly string[]): string {
  return argv.map((arg) => (IDENTITY_SAFE_ARG_RE.test(arg) ? arg : JSON.stringify(arg))).join(" ");
}

type ResolveSessionCwdOptions = {
  platform?: NodeJS.Platform;
  existsSync?: (filePath: string) => boolean;
  runWslpath?: (cwd: string) => Promise<string>;
};

export function isoNow(): string {
  return new Date().toISOString();
}

export function waitForSpawn(child: ChildProcess): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSpawn = () => {
      child.off("error", onError);
      resolve();
    };
    const onError = (error: Error) => {
      child.off("spawn", onSpawn);
      reject(error);
    };

    child.once("spawn", onSpawn);
    child.once("error", onError);
  });
}

export function isChildProcessRunning(child: ChildProcess): boolean {
  return child.exitCode == null && child.signalCode == null;
}

export function requireAgentStdio(
  child: ChildProcess,
): ChildProcessByStdio<Writable, Readable, Readable> {
  if (!child.stdin || !child.stdout || !child.stderr) {
    throw new Error("ACP agent must be spawned with piped stdin/stdout/stderr");
  }
  return child as ChildProcessByStdio<Writable, Readable, Readable>;
}

export function waitForChildExit(child: ChildProcess, timeoutMs: number): Promise<boolean> {
  if (!isChildProcessRunning(child)) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const timer = setTimeout(
      () => {
        finish(false);
      },
      Math.max(0, timeoutMs),
    );

    const finish = (value: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      child.off("close", onExitLike);
      child.off("exit", onExitLike);
      clearTimeout(timer);
      resolve(value);
    };

    const onExitLike = () => {
      finish(true);
    };

    child.once("close", onExitLike);
    child.once("exit", onExitLike);
  });
}

export function resolveAgentCommandParts(
  value: string,
  argv: readonly string[] | undefined,
  platform: NodeJS.Platform = process.platform,
): CommandParts {
  if (argv) {
    const parts = toCommandParts([...argv]);
    assertWindowsLaunchableCommand(parts.command, platform);
    return parts;
  }
  if (platform === "win32") {
    throw new Error(
      'Raw agent command strings are not supported on Windows. Configure the agent with an argv array, for example: "argv": ["agent.exe", "--acp"]. Legacy agents.<name>.args arrays are migrated automatically. Existing sessions without saved argv must be closed and recreated.',
    );
  }
  return splitCommandLine(value);
}

function assertWindowsLaunchableCommand(command: string, platform: NodeJS.Platform): void {
  if (platform === "win32" && path.extname(command).toLowerCase() === ".sh") {
    throw new Error(
      `Windows cannot launch shell script executable "${command}" directly. Configure an explicit interpreter argv, for example: "argv": ["bash", "${command}"]. acpx does not infer interpreters.`,
    );
  }
}

type CommandLineState = {
  current: string;
  quote: "'" | '"' | null;
  escaping: boolean;
  parts: string[];
  hasPart: boolean;
};

export function splitCommandLine(value: string): CommandParts {
  const state: CommandLineState = {
    current: "",
    quote: null,
    escaping: false,
    parts: [],
    hasPart: false,
  };
  for (const ch of value) {
    readCommandLineChar(state, ch);
  }

  if (state.escaping) {
    state.current += "\\";
    state.hasPart = true;
  }
  if (state.quote) {
    throw new Error("Invalid --agent command: unterminated quote");
  }
  flushCommandLinePart(state);
  return toCommandParts(state.parts);
}

function toCommandParts(parts: string[]): CommandParts {
  if (parts.length === 0 || parts[0] === "") {
    throw new Error("Invalid --agent command: empty command");
  }
  return {
    command: parts[0],
    args: parts.slice(1),
  };
}

function readCommandLineChar(state: CommandLineState, ch: string): void {
  if (state.escaping) {
    state.current += ch;
    state.escaping = false;
    state.hasPart = true;
  } else if (ch === "\\" && state.quote !== "'") {
    state.escaping = true;
  } else if (state.quote) {
    if (ch === state.quote) {
      state.quote = null;
    } else {
      state.current += ch;
    }
    state.hasPart = true;
  } else {
    readUnquotedCommandLineChar(state, ch);
  }
}

function readUnquotedCommandLineChar(state: CommandLineState, ch: string): void {
  if (ch === "'" || ch === '"') {
    state.quote = ch;
    state.hasPart = true;
  } else if (/\s/.test(ch)) {
    flushCommandLinePart(state);
  } else {
    state.current += ch;
    state.hasPart = true;
  }
}

function flushCommandLinePart(state: CommandLineState): void {
  if (state.hasPart) {
    state.parts.push(state.current);
    state.current = "";
    state.hasPart = false;
  }
}

export function asAbsoluteCwd(cwd: string): string {
  return path.resolve(cwd);
}

export async function resolveAgentSessionCwd(
  cwd: string,
  agentCommand: string,
  options: ResolveSessionCwdOptions = {},
): Promise<string> {
  const resolved = asAbsoluteCwd(cwd);
  if (!shouldTranslateWslWindowsCwd(agentCommand, options)) {
    return resolved;
  }

  const translated = (await (options.runWslpath ?? runWslpath)(resolved)).trim();
  if (!translated) {
    throw new Error(`wslpath returned an empty Windows path for cwd: ${resolved}`);
  }
  return translated;
}

function shouldTranslateWslWindowsCwd(
  agentCommand: string,
  options: ResolveSessionCwdOptions,
): boolean {
  if (!isWsl(options)) {
    return false;
  }

  try {
    const { command } = splitCommandLine(agentCommand);
    return isWindowsExecutableCommand(command);
  } catch {
    return false;
  }
}

function isWsl(options: ResolveSessionCwdOptions): boolean {
  const platform = options.platform ?? process.platform;
  if (platform !== "linux") {
    return false;
  }

  const existsSync = options.existsSync ?? fs.existsSync;
  return existsSync("/proc/sys/fs/binfmt_misc/WSLInterop");
}

const WINDOWS_EXECUTABLE_EXTENSION_RE = /\.(?:exe|cmd|bat)$/u;

function isWindowsExecutableCommand(command: string): boolean {
  const normalized = command.toLowerCase();
  return WINDOWS_EXECUTABLE_EXTENSION_RE.test(normalized);
}

async function runWslpath(cwd: string): Promise<string> {
  return await runTimedExecFile("wslpath", ["-w", cwd]);
}

export function basenameToken(value: string): string {
  return path
    .basename(value)
    .toLowerCase()
    .replace(/\.(cmd|exe|bat)$/u, "");
}
